import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Mic, Users } from 'lucide-react';

const FOREST = '#2C4A3B';
const FOREST_DARK = '#1E3329';
const TERRA = '#C47A5E';
const CREAM = '#F9F6F0';
const CHARCOAL = '#1C1917';

export default function RegisterPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const invitedEmail = searchParams.get('email');
  const [formData, setFormData] = useState({
    email: invitedEmail || '',
    password: '',
    full_name: '',
    role: 'provider',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      onLogin(response.data.token, response.data.user);

      // If registering from an invite link, accept the invitation
      // immediately so the new user lands directly inside the practice.
      if (inviteToken) {
        try {
          await axios.post(
            `${API}/practice/invitation/${inviteToken}/accept`,
            {},
            { headers: { Authorization: `Bearer ${response.data.token}` } },
          );
          toast.success('Account created — you joined your practice!');
          navigate('/practice');
          return;
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Account created, but invitation could not be accepted.');
          navigate('/');
          return;
        }
      }

      toast.success('Account created successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = { borderColor: '#E3E8E4' };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ fontFamily: "'Manrope', sans-serif", background: CREAM }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.06]" style={{ background: FOREST }} aria-hidden="true" />
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-[0.08]" style={{ background: TERRA }} aria-hidden="true" />

      <div className="w-full max-w-md relative">
        <div className="rounded-3xl shadow-xl p-10 border" style={{ background: '#fff', borderColor: '#E3E8E4' }}>
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md" style={{ background: FOREST }}>
              <Mic className="w-8 h-8" style={{ color: CREAM }} />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-center mb-2 tracking-tight" style={{ color: CHARCOAL }}>
            Plexura AI Scribe
          </h1>
          <p className="text-center text-sm mb-8 flex items-center justify-center gap-2" style={{ color: '#6B8E7B' }}>
            <Shield className="w-4 h-4" />
            HIPAA-Compliant AI Medical Scribe
          </p>

          {inviteToken && (
            <div
              data-testid="register-invite-banner"
              className="mb-6 rounded-xl border p-3 flex items-center gap-3"
              style={{ borderColor: TERRA, background: '#FDECE2' }}
            >
              <Users className="w-5 h-5 shrink-0" style={{ color: TERRA }} />
              <p className="text-xs leading-snug" style={{ color: CHARCOAL }}>
                You've been invited to join a practice. Create your account and we'll add you automatically.
              </p>
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="full_name" className="text-sm font-medium" style={{ color: CHARCOAL }}>Full Name</Label>
              <Input id="full_name" name="full_name" type="text" value={formData.full_name} onChange={handleChange}
                placeholder="Dr. John Smith" required data-testid="fullname-input"
                className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2" style={fieldStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = FOREST; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#E3E8E4'; }} />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium" style={{ color: CHARCOAL }}>Email Address</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                placeholder="provider@example.com" required data-testid="email-input"
                className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2" style={fieldStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = FOREST; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#E3E8E4'; }} />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium" style={{ color: CHARCOAL }}>Password</Label>
              <Input id="password" name="password" type="password" value={formData.password} onChange={handleChange}
                placeholder="••••••••" required data-testid="password-input"
                className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2" style={fieldStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = FOREST; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#E3E8E4'; }} />
            </div>

            <Button type="submit" disabled={loading} data-testid="register-button"
              className="w-full h-11 rounded-xl text-white font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: loading ? FOREST_DARK : FOREST }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = FOREST_DARK; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = FOREST; }}>
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-sm mt-6" style={{ color: '#6B8E7B' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-semibold hover:underline" style={{ color: TERRA }} data-testid="login-link">
              Sign in here
            </Link>
          </p>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: '#6B8E7B' }}>
          © Plexura AI Scribe · HIPAA-compliant mental health scribing
        </p>
      </div>
    </div>
  );
}
